import { Cartesian3 } from "cesium";
import { EarthBrainUtils } from "./earthbrainutility";
export var SimaUtils;
(function (SimaUtils) {
    /**
     * Computes Circle center & vector normal to the circle from 2 points on a circle arc
     * @param p1 point 1 on circle
     * @param p2 point 2 on circle
     * @param radius signed radius of the circle (positive for left turn / CCW, negative for right turn / CW)
     * @returns Circle Center & vector normal to the circle
     */
    function getCircleCenterFrom2PointsOnCircleArc(p1, p2, radius) {
        const p1OnDatum = new Cartesian3(p1.x, p1.y, 0);
        const p2OnDatum = new Cartesian3(p2.x, p2.y, 0);
        const chordLength = Cartesian3.distance(p1OnDatum, p2OnDatum);
        if (chordLength < 1e-9 || radius === 0) {
            return {
                circleCenter: new Cartesian3(p1.x, p1.y, (p1.z + p2.z) * 0.5),
                normal: new Cartesian3(0, 0, radius >= 0 ? 1 : -1),
            };
        }
        const chordDirection = EarthBrainUtils.getNormalizedDirection(p1OnDatum, p2OnDatum);
        const normal = new Cartesian3(0, 0, radius >= 0 ? 1 : -1);
        const directionToCenter = new Cartesian3();
        if (radius > 0) {
            Cartesian3.cross(chordDirection, new Cartesian3(0, 0, 1), directionToCenter);
        }
        else {
            Cartesian3.cross(new Cartesian3(0, 0, 1), chordDirection, directionToCenter);
        }
        Cartesian3.normalize(directionToCenter, directionToCenter);
        const absRadius = Math.abs(radius);
        const halfChord = chordLength * 0.5;
        const radSqMinusChordSq = Math.max(0, Math.pow(absRadius, 2) - Math.pow(halfChord, 2));
        const lengthOfLinePerpendicularToChord = Math.sqrt(radSqMinusChordSq);
        const chordMiddlePoint = EarthBrainUtils.getCartesianPointAtDistanceFromCartesian(p1OnDatum, chordDirection, halfChord);
        const circleCenter = EarthBrainUtils.getCartesianPointAtDistanceFromCartesian(chordMiddlePoint, directionToCenter, lengthOfLinePerpendicularToChord);
        circleCenter.z = (p1.z + p2.z) * 0.5;
        return { circleCenter, normal };
    }
    SimaUtils.getCircleCenterFrom2PointsOnCircleArc = getCircleCenterFrom2PointsOnCircleArc;
    /**
     * Create arc from 2 points on a circle
     * @param p1 point 1 on circle
     * @param p2 point 2 on circle
     * @param radius signed radius of the circle (positive: left curve, negative: right curve)
     * @param isLargeCircle Boolean True -> take the larger arc of the circle False -> take the smaller arc of the circle
     * @param stepSize step distance in meters along the arc (default 0.5m)
     * @returns Cartesian3[] array of intermediate points between p1 and p2 (exclusive)
     */
    function createArcFrom2PointsOnACircle(p1, p2, radius, isLargeCircle, stepSize = 0.5) {
        if (radius === 0)
            return [];
        const { circleCenter } = getCircleCenterFrom2PointsOnCircleArc(p1, p2, radius);
        const absRadius = Math.abs(radius);
        const theta1 = Math.atan2(p1.y - circleCenter.y, p1.x - circleCenter.x);
        const chordLength = Math.hypot(p2.x - p1.x, p2.y - p1.y);
        const sinHalfAngle = Math.min(1, Math.max(-1, chordLength / (2 * absRadius)));
        const minorCentralAngle = 2 * Math.asin(sinHalfAngle);
        let sweepAngle = isLargeCircle
            ? 2 * Math.PI - minorCentralAngle
            : minorCentralAngle;
        if (radius > 0) {
            sweepAngle = -sweepAngle;
        }
        const arcLength = absRadius * Math.abs(sweepAngle);
        const numSegments = Math.max(2, Math.ceil(arcLength / stepSize));
        const intermediatePoints = [];
        for (let i = 1; i < numSegments; i++) {
            const t = i / numSegments;
            const theta = theta1 + t * sweepAngle;
            const x = circleCenter.x + absRadius * Math.cos(theta);
            const y = circleCenter.y + absRadius * Math.sin(theta);
            const z = p1.z + t * (p2.z - p1.z);
            intermediatePoints.push(new Cartesian3(x, y, z));
        }
        return intermediatePoints;
    }
    SimaUtils.createArcFrom2PointsOnACircle = createArcFrom2PointsOnACircle;
    /**
     * Generate points of a clothoid curve between straight line to an arc of a circle with given radius
     * Based on Application: Connecting an oriented straight stretch with an arc of an oriented circle of the following research paper
     * https://www.researchgate.net/publication/292669884_The_Clothoid_Computation_A_Simple_and_Efficient_Numerical_Algorithm
     * @param A Scale Factor of the curve
     * @param circleCenter Circle
     * @param radius Radius of Circle
     * @param pointOnStraightLine point of contact with straight line & clothoid curve
     * @param linkPoint point of contact with simple curve & clothoid curve
     * @param V Straight Line Vector
     * @param OX X Basis Vector
     * @returns Cartesian3[]
     */
    function generateClothoidCurveForStraightLineToCurve(A, 
    // circleCenter: Cartesian3,
    radius, pointOnStraightLine, linkPoint, V, OX) {
        // Compute the next point based on the previous point.
        const computeXY = (prevIterationResult, lambda, A, phiNot, deltaS, n) => {
            // x^N = xsubscriptF
            // y^N = ysubscriptF
            // s^n = n * deltaS
            return {
                x: prevIterationResult.x -
                    deltaS *
                        Math.cos((lambda * Math.pow(n * deltaS, 2)) / (2 * Math.pow(A, 2)) +
                            phiNot),
                y: prevIterationResult.y -
                    deltaS *
                        Math.sin((lambda * Math.pow(n * deltaS, 2)) / (2 * Math.pow(A, 2)) +
                            phiNot),
            };
        };
        console.log("generateClothoidCurveForStraightLineToCurve Parameters", A, 
        // circleCenter,
        radius, pointOnStraightLine, linkPoint, V, OX);
        const lambda = Math.sign(radius);
        const phiNot = Cartesian3.angleBetween(V, OX);
        // TODO Check L
        // const C0 = new Cartesian3(circleCenter.x, circleCenter.y, 0);
        // const F0 = new Cartesian3(linkPoint.x, linkPoint.y, 0);
        // const C0F0 = EarthBrainUtils.getDirBetweenTwoCartesians(C0, F0);
        // const vTau = new Cartesian3();
        // Cartesian3.cross(new Cartesian3(0, 0, -1), C0F0, vTau);
        // const alpha = Cartesian3.angleBetween(OX, vTau);
        // const tau = lambda * (alpha - phiNot) >= 0 ? lambda * (alpha - phiNot) : 2 * Math.PI + lambda * (alpha - phiNot);
        const L = Math.abs((A * A) / radius);
        // console.log("Check Length of Clothoid Curve", (A * A) / radius, 2 * radius * tau, "Error: ", (Math.abs((A * A) / radius) - Math.abs(2 * radius * tau)) / Math.abs((A * A) / radius) * 100, "%");
        // const N = Math.pow(10, Math.round(Math.log10(L) + 2));
        const deltaS = 0.5;
        const N = L / deltaS;
        console.log(`Sample Size N for fixed delta S ${deltaS} is`, N);
        // Initial value or the previous iteration result is the End point/Link point of the clothoid curve
        let prevIterationResult = {
            x: linkPoint.x,
            y: linkPoint.y,
        };
        // Parallelity: 2 lines are parallel => 1, 2 lines are perpendicular => 0 Basically the modulus of dot product of 2 line vectors
        const curvePointObjects = [];
        const SP0 = new Cartesian3(pointOnStraightLine.x, pointOnStraightLine.y, 0);
        const LP0 = new Cartesian3(linkPoint.x, linkPoint.y, 0);
        const directionSP0LP0 = EarthBrainUtils.getDirBetweenTwoCartesians(SP0, LP0);
        const directionSPLP = EarthBrainUtils.getDirBetweenTwoCartesians(pointOnStraightLine, linkPoint);
        // Angle between SP & S0P0
        const declinationAngle = Cartesian3.angleBetween(directionSPLP, directionSP0LP0);
        const deltaH = deltaS * Math.tan(declinationAngle);
        let LPHeight = linkPoint.z;
        console.log(`Height Interpolation from ${LPHeight} to ${pointOnStraightLine.z} with Delta H ${deltaH}`);
        // Link Point To point before Straight Line Point
        for (let n = N - 1; n > 0; n--) {
            prevIterationResult = computeXY(prevIterationResult, lambda, A, phiNot, deltaS, n);
            LPHeight += deltaH;
            const clothoidCurvePoint = new Cartesian3(prevIterationResult.x, prevIterationResult.y, LPHeight);
            const clothoidPtStraightLinePointDirection = EarthBrainUtils.getDirBetweenTwoCartesians(clothoidCurvePoint, pointOnStraightLine);
            const parallelity = Math.abs(Cartesian3.dot(clothoidPtStraightLinePointDirection, V));
            curvePointObjects.push({ point: clothoidCurvePoint, parallelity });
        }
        let maxParallelity = 0;
        let indexOfPointWithHighestParallelity = 0;
        for (let index = 0; index < curvePointObjects.length; index++) {
            const { parallelity } = curvePointObjects[index];
            if (parallelity > maxParallelity) {
                indexOfPointWithHighestParallelity = index;
            }
            maxParallelity = Math.max(parallelity, maxParallelity);
        }
        console.log(`Point with index ${indexOfPointWithHighestParallelity} has the highest parallelity of ${maxParallelity}`);
        const curvePoints = curvePointObjects
            .slice(0, indexOfPointWithHighestParallelity + 1)
            .map((cPO) => cPO.point);
        return curvePoints;
    }
    SimaUtils.generateClothoidCurveForStraightLineToCurve = generateClothoidCurveForStraightLineToCurve;
    /**
     * Generate points of a clothoid curve between an arc of a circle with given radius to a straight line
     * Based on Application: Connecting an oriented straight stretch with an arc of an oriented circle of the following research paper
     * https://www.researchgate.net/publication/292669884_The_Clothoid_Computation_A_Simple_and_Efficient_Numerical_Algorithm
     * @param A Scale Factor of the curve
     * @param circleCenter Circle
     * @param radius Radius of Circle
     * @param pointOnStraightLine Point of contact with Straight Line & Clothoid Curve
     * @param linkPoint Point of contact with Simple curve & Clothoid Curve
     * @param V Straight Line Vector
     * @param OX X Basis Vector
     * @returns Cartesian3[]
     */
    function generateClothoidCurveForCurveToStraightLine(A, circleCenter, radius, pointOnStraightLine, linkPoint, V, OX) {
        // Compute the next point based on the previous point.
        const computeXY = (prevIterationResult, lambda, A, phiNot, deltaS, n) => {
            // x^N = xsubscriptF
            // y^N = ysubscriptF
            // s^n = n * deltaS
            return {
                x: prevIterationResult.x +
                    deltaS *
                        Math.cos((lambda * Math.pow(n * deltaS, 2)) / (2 * Math.pow(A, 2)) +
                            phiNot),
                y: prevIterationResult.y +
                    deltaS *
                        Math.sin((lambda * Math.pow(n * deltaS, 2)) / (2 * Math.pow(A, 2)) +
                            phiNot),
            };
        };
        console.log("generateClothoidCurveForCurveToStraightLine Parameters", A, circleCenter, radius, pointOnStraightLine, linkPoint, V, OX);
        const lambda = Math.sign(radius);
        const phiNot = Cartesian3.angleBetween(V, OX);
        // TODO Check L
        // const C0 = new Cartesian3(circleCenter.x, circleCenter.y, 0);
        // const F0 = new Cartesian3(linkPoint.x, linkPoint.y, 0);
        // const C0F0 = EarthBrainUtils.getDirBetweenTwoCartesians(C0, F0);
        // const vTau = new Cartesian3();
        // Cartesian3.cross(new Cartesian3(0, 0, -1), C0F0, vTau);
        // const alpha = Cartesian3.angleBetween(OX, vTau);
        // const tau = lambda * (alpha - phiNot) >= 0 ? lambda * (alpha - phiNot) : 2 * Math.PI + lambda * (alpha - phiNot);
        const L = Math.abs((A * A) / radius);
        // console.log("Check Length of Clothoid Curve", (A * A) / radius, 2 * radius * tau, "Error: ", (Math.abs((A * A) / radius) - Math.abs(2 * radius * tau)) / Math.abs((A * A) / radius) * 100, "%");
        // const N = Math.pow(10, Math.round(Math.log10(L) + 2));
        const deltaS = 0.5;
        const N = L / deltaS;
        console.log(`Sample Size N for fixed delta S ${deltaS} is`, N);
        // Initial value or the previous iteration result is the End point/Link point of the clothoid curve
        let prevIterationResult = {
            x: pointOnStraightLine.x,
            y: pointOnStraightLine.y,
        };
        // Parallelity: 2 lines are parallel => 1, 2 lines are perpendicular => 0 Basically the modulus of dot product of 2 line vectors
        const curvePointObjects = [];
        const SP0 = new Cartesian3(pointOnStraightLine.x, pointOnStraightLine.y, 0);
        const LP0 = new Cartesian3(linkPoint.x, linkPoint.y, 0);
        const directionSP0LP0 = EarthBrainUtils.getDirBetweenTwoCartesians(SP0, LP0);
        const directionSPLP = EarthBrainUtils.getDirBetweenTwoCartesians(pointOnStraightLine, linkPoint);
        // Angle between SP & S0P0
        const declinationAngle = Cartesian3.angleBetween(directionSPLP, directionSP0LP0);
        const deltaH = deltaS * Math.tan(declinationAngle);
        let SPHeight = pointOnStraightLine.z;
        console.log(`Height Interpolation from ${SPHeight} to ${linkPoint.z} with Delta H ${deltaH}`);
        // Straight Line Point To point before Link Point
        for (let n = 0; n < N - 1; n++) {
            prevIterationResult = computeXY(prevIterationResult, lambda, A, phiNot, deltaS, n);
            SPHeight += deltaH;
            const clothoidCurvePoint = new Cartesian3(prevIterationResult.x, prevIterationResult.y, SPHeight);
            const clothoidPtStraightLinePointDirection = EarthBrainUtils.getDirBetweenTwoCartesians(clothoidCurvePoint, pointOnStraightLine);
            const parallelity = Math.abs(Cartesian3.dot(clothoidPtStraightLinePointDirection, V));
            curvePointObjects.push({ point: clothoidCurvePoint, parallelity });
        }
        let maxParallelity = 0;
        let indexOfPointWithHighestParallelity = 0;
        for (let index = 0; index < curvePointObjects.length; index++) {
            const { parallelity } = curvePointObjects[index];
            if (parallelity > maxParallelity) {
                indexOfPointWithHighestParallelity = index;
            }
            maxParallelity = Math.max(parallelity, maxParallelity);
        }
        console.log(`Point with index ${indexOfPointWithHighestParallelity} has the highest parallelity of ${maxParallelity}`);
        const curvePoints = curvePointObjects
            .slice(indexOfPointWithHighestParallelity)
            .map((cPO) => cPO.point);
        return curvePoints;
    }
    SimaUtils.generateClothoidCurveForCurveToStraightLine = generateClothoidCurveForCurveToStraightLine;
})(SimaUtils || (SimaUtils = {}));
//# sourceMappingURL=sima-utils.js.map